import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import statsUsaData from '../../assets/json/statesUSA.json';
import { commonTermsApi, configApi } from '../../services/rest/apiInstances';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';
import { addNotification } from '@/reduxStore/slices/systemNotificationSlice';

export const fetchCountriesList = createAsyncThunk('commonTerms/fetchCountriesList', async () => {
    try {
        const data = await commonTermsApi.getCountries();
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});
export const fetchMccList = createAsyncThunk('commonTerms/fetchMccList', async () => {
    try {
        const data = await commonTermsApi.getMerchantCategoryCodes();
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});
export const fetchStateUsa = createAsyncThunk('commonTerms/fetchStateUsa', async () => {
    try {
        return statsUsaData;
    } catch (error: any) {
        return error.data;
    }
});

export const fetchClientConfiguration = createAsyncThunk(
    'commonTerms/fetchClientConfiguration',
    async (_, thunkAPI) => {
        try {
            const options = buildClientApiConfig();
            const data = await configApi.getConfig(options);

            return data.data;
        } catch (error: any) {
            if (error?.response?.status == '404') {
                thunkAPI.dispatch(
                    addNotification({
                        message: 'Invalid client id. Client configuration not found',
                        status: 'failure',
                    }),
                );
            }
            return error.data;
        }
    },
);

export interface ITransactionsState {
    isPending: boolean;
    errors: any;
    countries: any[];
    mccList: any[];
    statesUsa: any[];
    config: any;
}

const initialState: ITransactionsState = {
    isPending: false,
    errors: null,
    countries: [],
    mccList: [],
    statesUsa: statsUsaData,
    config: null,
};

export const commonTermsSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCountriesList.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchCountriesList.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchCountriesList.fulfilled, (state, action) => {
                state.isPending = false;
                state.countries = action.payload;
            })
            .addCase(fetchClientConfiguration.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchClientConfiguration.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchClientConfiguration.fulfilled, (state, action) => {
                state.isPending = false;
                const enabledProductTypes = action.payload.enabledCardProducts;
                const enabledCurrencies = action.payload.currencySettings.map(
                    (c: any) => c.currency,
                );
                function createCardSchemaNames(enabledCardProducts: any[]): string[] {
                    return Array.from(
                        new Set(enabledCardProducts.map((product) => product.scheme)),
                    );
                }
                action.payload.schemeList = createCardSchemaNames(
                    action.payload.enabledCardProducts,
                );
                action.payload.enabledCurrencies = enabledCurrencies;
                state.config = action.payload;
            })
            .addCase(fetchMccList.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchMccList.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchMccList.fulfilled, (state, action) => {
                state.isPending = false;
                const list: { id: string; title: string; groupId: string }[] = [];
                action.payload?.map(({ group, codes }: { group: string; codes: any[] }) => {
                    codes.map((code: any) => {
                        list.push({
                            groupId: group,
                            id: code.code,
                            title: `${code.code} ${code.name}`,
                        });
                    });
                });

                state.mccList = list;
            })
            .addCase(fetchStateUsa.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchStateUsa.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchStateUsa.fulfilled, (state, action) => {
                state.isPending = false;
                state.countries = action.payload;
            });
    },
});

export default commonTermsSlice.reducer;
